import React, { useEffect, useState } from "react";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { Paper, Stack, Typography } from "@mui/material";
import NoItemsWasFound from "../no-items";
import { IDataItem } from "@/api/types";

interface IDonutChartProps {
  title: string;
  properties: IDataItem[];
  selectedStatus: string;
}

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, "0");
  }
  return color;
};

const DonutChart: React.FunctionComponent<IDonutChartProps> = ({
  title,
  properties,
  selectedStatus,
}) => {
  // Define your custom palette
  const customPalette = ["#3D348B", "#F35B04", "#7678ED", "#F18701", "#F7B801"];

  // Filter and prepare data
  const filteredProperties = properties.filter(
    (item) =>
      !isNaN(parseFloat(item.user_investments)) &&
      item.symbol_fa !== null &&
      (
        item?.event_last?.title !== "حسابرسی" &&
        item?.event_last?.type !== "news"
      )
  );

  const data = filteredProperties.map((item, index) => ({
    name: item.symbol_fa,
    value: parseFloat(item.user_investments),
    yearly_wage: parseFloat(item.yearly_wage).toString(),
    itemStyle: { color: customPalette[index % customPalette.length] },
    userInvestments: item.user_investments,
  }));

  const showChart = data.length > 0;

  // ECharts option
  const optionold: EChartsOption = {
    tooltip: {
      trigger: "item" as "item",
      formatter: (params: any) => {
        const yearlyWage = params.value;
        return `${params.name}\nسود: ${yearlyWage}%`;
      },
    },
    legend: {
      show: false,
      orient: "vertical",
      left: "right",
    },
    series: [
      {
        name: "",
        type: "pie" as "pie",
        radius: ["50%", "70%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          position: "center",
          backgroundColor: "#fff", // White background
          borderWidth: 1,
          borderRadius: 5,
          rich: {
            // Using rich text formatting for custom styles
            labelText: {
              fontSize: 16,
              fontWeight: "bold",
              color: "#333",
            },
          },
          formatter: (params: any) => {
            const yearlyWage = params.value;
            return `{labelText|${params.name}\nسود: ${yearlyWage}%}`; // Use rich text formatting
          },
        },
        emphasis: {
          scale: true, // This will enable the scaling effect on hover
          itemStyle: {
            borderWidth: 3,
            borderColor: "#fff",
          },
          label: {
            show: true,
            fontSize: 240, // Larger font size when hovered
            fontWeight: "bold",
            color: "#333", // You can change color too when hovered
          },
        },
        labelLine: {
          show: true,
        },
        data: data.map((item) => ({
          value: item.value,
          name: item.name,
        })),
      },
    ],
  };
  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        const value = params.value;
        return `${value.withCommas().withPriceUnit()}`;
      },
    },
    legend: {
      show: false,
      orient: "vertical",
      left: "right",
    },
    series: [
      {
        name: "",
        type: "pie",
        radius: ["40%", "70%"], // Inner and outer radius for donut
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10, // Rounded corners
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true, // Ensure the label is displayed
          position: "center", // Position the label in the center
          formatter: function (params) {
            // Access yearly_wage and display it
            const yearlyWage = params.data.yearly_wage;
            const name = params.data.name;
            return `${name}\nسود: ${yearlyWage}%`;
          },
          backgroundColor: "#fff", // White background
          borderWidth: 0, // Optional: Border width
          borderRadius: 5, // Optional: Rounded corners
          textStyle: {
            fontSize: 12, // Font size of the text
            fontWeight: "bold", // Font weight
            color: "#333", // Text color
            textAlign: "center", // Align the text
            fontFamily: "Vazir", // ✅ Use Vazir font here
          },
        },
        emphasis: {
          label: {
            show: true,
            fontSize: "16",
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: true,
        },
        data, // Ensure yearly_wage is part of this data
      },
    ],
  };

  return (
    <Paper
      sx={{
        height: "100%",
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "column",
        [`& *`]: {
          minHeight: "0 !important",
          fontFamily: "Vazir !important",
        },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="bodyMedium">{title}</Typography>
        {showChart ? (
          <ReactECharts option={option} style={{ height: 400 }} />
        ) : (
          <NoItemsWasFound />
        )}
      </Stack>
    </Paper>
  );
};

export default DonutChart;
